import { pickActiveId } from "@arcadeai/ui-kit/org-project-context/resolve";
import type { OrgProjectContextOption } from "@arcadeai/ui-kit/org-project-context/types";
import {
  type UseSuspenseQueryOptions,
  type UseSuspenseQueryResult,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ORG_PROJECT_ROUTE_PATH } from "@/lib/org-project-context/constants";
import {
  getProjectsQueryOptions,
  orgProjectKeys,
} from "@/lib/org-project-context/coordinator-query";
import {
  useDashboardOrgProject,
  useOrgProjectRouteContext,
  useScopeKey,
} from "@/lib/org-project-context/hooks";
import type {
  CreateProjectFormData,
  PartialUpdateProjectFormData,
  Project,
} from "../types";
import { createProject } from "./create-project";
import { deleteProject } from "./delete-project";
import { updateProject } from "./update-project";

function toDashboardProjects(
  items: OrgProjectContextOption[],
  organizationId: string
): Project[] {
  return items.map((o) => ({
    project_id: o.id,
    tenant_id: organizationId,
    name: o.name,
    description: o.description,
    is_default: o.isDefault,
    created_at: o.createdAt ?? "",
    can_edit: o.canEdit,
    can_invite: o.canInvite,
  }));
}

type UseGetProjectsOptions = Omit<
  UseSuspenseQueryOptions<OrgProjectContextOption[], Error, Project[]>,
  "queryKey" | "queryFn" | "select"
>;

export const useGetProjects = (
  organizationId: string,
  options?: UseGetProjectsOptions
): UseSuspenseQueryResult<Project[], Error> => {
  const scopeKey = useScopeKey();
  return useSuspenseQuery({
    ...getProjectsQueryOptions(organizationId, scopeKey),
    select: (items) => toDashboardProjects(items, organizationId),
    ...options,
  });
};

export const useCreateProject = (organizationId: string) => {
  const queryClient = useQueryClient();
  const scopeKey = useScopeKey();
  const navigate = useNavigate({ from: ORG_PROJECT_ROUTE_PATH });
  const { activeOrganizationId } = useDashboardOrgProject();

  return useMutation({
    mutationFn: (project: CreateProjectFormData) =>
      createProject(project, organizationId),
    meta: {
      invalidates: [orgProjectKeys.projects(scopeKey, organizationId)],
      errorMessage: "Failed to create project",
    },
    onSuccess: async (data) => {
      toast.success("Project created successfully");

      if (organizationId === activeOrganizationId) {
        await navigate({
          params: () => ({
            organizationId,
            projectId: data.project_id,
          }),
          to: "./home",
        });
      } else {
        await queryClient.prefetchQuery(
          getProjectsQueryOptions(organizationId, scopeKey)
        );
      }
    },
  });
};

export const useDeleteProject = (organizationId: string) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: ORG_PROJECT_ROUTE_PATH });
  const scopeKey = useScopeKey();
  const { projectId: activeProjectId } = useOrgProjectRouteContext();
  const { activeOrganizationId } = useDashboardOrgProject();

  return useMutation({
    mutationFn: (projectId: string) =>
      deleteProject(projectId, organizationId, activeProjectId),
    meta: {
      invalidates: [orgProjectKeys.projects(scopeKey, organizationId)],
      errorMessage: "Failed to delete project",
    },
    onSuccess: async (data, deletedProjectId) => {
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("Project deleted successfully");

      if (
        deletedProjectId === activeProjectId &&
        organizationId === activeOrganizationId
      ) {
        const remainingProjects = await queryClient.fetchQuery({
          ...getProjectsQueryOptions(organizationId, scopeKey),
          staleTime: 0,
        });
        const nextProjectId = pickActiveId(remainingProjects);

        if (nextProjectId) {
          await navigate({
            params: () => ({
              organizationId,
              projectId: nextProjectId,
            }),
            to: "./home",
            replace: true,
          });
        } else {
          await navigate({ to: "/", replace: true });
        }
      }
    },
  });
};

export const useUpdateProject = (organizationId: string) => {
  const queryClient = useQueryClient();
  const scopeKey = useScopeKey();

  return useMutation({
    mutationFn: (data: PartialUpdateProjectFormData) =>
      updateProject(data, organizationId),
    meta: {
      invalidates: [orgProjectKeys.projects(scopeKey, organizationId)],
    },
    onSuccess: (updatedProject: Project) => {
      toast.success("Project updated successfully");

      const listKey = orgProjectKeys.projects(scopeKey, organizationId);

      queryClient.setQueryData<OrgProjectContextOption[] | undefined>(
        listKey,
        (old) =>
          old?.map((row) =>
            row.id === updatedProject.project_id
              ? {
                  ...row,
                  name: updatedProject.name,
                  description: updatedProject.description ?? null,
                  isDefault: updatedProject.is_default,
                }
              : row
          ) ?? old
      );
    },
    onError: (error) => {
      console.error(error);
      toast.error("Failed to update project");
    },
  });
};
